import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { BlogArticle } from 'routes/Home/models/BlogArticle';
import Flex from 'ui/Flex/Flex';

const StyDateTypo = styled(Typography, {
    name: 'StyDateTypo',
})(({ theme }) => ({
    color: theme.palette.mediumGrey,
}));

const StyOverlay = styled('div', {
    name: 'StyOverlay',
})(() => ({
    backgroundImage:
        'linear-gradient(114deg,rgba(0,0,0,.5) 35%,rgba(13,14,16,.4))',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
}));

const StyBlogAuthor = styled('div', {
    name: 'StyBlogAuthor',
})(() => ({
    position: 'absolute',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    top: '8px',
    left: '8px',
    '& p': {
        color: 'white',
        fontWeight: 700,
    },
}));

const StyImg = styled('img', {
    name: 'StyImg',
})(() => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    marginLeft: 'auto',
    marginRight: 'auto',
}));

const StyAuthorImg = styled('img', {
    name: 'StyAuthorImg',
})(() => ({
    width: '48px',
    height: '48px',
    borderRadius: '60%',
    border: '1px solid white',
    marginRight: '16px',
}));

const StyImageContainer = styled('div', {
    name: 'StyImageContainer',
})(() => ({
    height: '232px',
    overflow: 'hidden',
    borderRadius: '12px',
    position: 'relative',
    marginBottom: '24px',
}));

const StyBlogDot = styled('div', {
    name: 'StyBlogDot',
})(({ theme }) => ({
    width: '5px',
    height: '5px',
    backgroundColor: theme.palette.mediumGrey,
    borderRadius: '60%',
    marginLeft: '10px',
    marginRight: '10px',
}));

const StyLink = styled('a', {
    name: 'StyLink',
})(() => ({
    maxWidth: '300px',
    width: '20%',
    minWidth: '200px',
    '&:hover #article-image > img': {
        transform: 'scale3d(1.1, 1.1, 1)',
        transformStyle: 'preserve-3d',
        transitionDuration: '500ms',
    },
    '#article-image > img': {
        transform: 'scale3d(1, 1, 1)',
        transformStyle: 'preserve-3d',
        transitionDuration: '500ms',
    },
}));

type Props = {
    blogArticle: BlogArticle;
};

const BlogArticleCard = ({ blogArticle }: Props): JSX.Element => {
    const publishedAtReadable = new Date(
        blogArticle.publishedAt,
    ).toLocaleDateString();

    return (
        <StyLink href={blogArticle.slug} target="_blank" rel="noreferrer">
            <Flex direction="column" align="start">
                <StyImageContainer id="article-image">
                    <StyImg
                        loading="lazy"
                        src={blogArticle.imageUrl}
                        alt={blogArticle.name}
                    />
                    <StyBlogAuthor>
                        <StyAuthorImg src={blogArticle.author.imageUrl} />
                        <Typography>{blogArticle.author.name}</Typography>
                    </StyBlogAuthor>
                    <StyOverlay />
                </StyImageContainer>
                <Typography sx={{ fontSize: '16px' }} variant="body1Bold">
                    {blogArticle.name}
                </Typography>
                <Flex align="center" sx={{ mt: 2 }}>
                    <StyDateTypo variant="body2">
                        {publishedAtReadable}
                    </StyDateTypo>
                    <StyBlogDot />
                    <StyDateTypo variant="body2">
                        {blogArticle.readTime}
                    </StyDateTypo>
                </Flex>
            </Flex>
        </StyLink>
    );
};

export default BlogArticleCard;
